import React, { useState, useEffect, useRef } from 'react';
import ModoLevantamiento from './ModoLevantamiento';
import AgenteBeep from './AgenteBeep';
import CanalAgentes from './CanalAgentes';

const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_ROJO = '#e05c5c';
const COLOR_VERDE = '#5cd4b0';
const PP = "'Poppins', sans-serif";
const STORAGE_KEY = 'beep_tareas_personales';

function cargarTareas() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function guardarTareas(tareas) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tareas));
    window.dispatchEvent(new Event('beep:tareas-actualizadas'));
  } catch {}
}

function getAgentesParaRol(rol) {
  const r = (rol || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const A = {
    aria:  { id:'aria',  nombre:'Aria',  funcion:'Tu asistente personal',      emoji:'👩‍💼', color:'#185FA5', colorFondo:'#E6F1FB' },
    beep:  { id:'beep',  nombre:'Beep',  funcion:'Director de orquesta',       emoji:'🎯', color:'#185FA5', colorFondo:'#E6F1FB' },
    axel:  { id:'axel',  nombre:'Axel',  funcion:'Ventas y cotizaciones',      emoji:'💼', color:'#0F6E56', colorFondo:'#E1F5EE' },
    max:   { id:'max',   nombre:'Max',   funcion:'Operaciones y servicios',    emoji:'⚙️', color:'#854F0B', colorFondo:'#FAEEDA' },
    rex:   { id:'rex',   nombre:'Rex',   funcion:'Proyectos e instalaciones',  emoji:'🏗️', color:'#0F6E56', colorFondo:'#E1F5EE' },
    bruno: { id:'bruno', nombre:'Bruno', funcion:'Almacén e inventario',       emoji:'📦', color:'#534AB7', colorFondo:'#EEEDFE' },
    nova:  { id:'nova',  nombre:'Nova',  funcion:'Administración y RH',        emoji:'📋', color:'#993556', colorFondo:'#FBEAF0' },
    luna:  { id:'luna',  nombre:'Luna',  funcion:'Marketing y campañas',       emoji:'✨', color:'#854F0B', colorFondo:'#FAEEDA' },
    vera:  { id:'vera',  nombre:'Vera',  funcion:'Asistente del Ing. Javier',  emoji:'👩‍🔬', color:'#534AB7', colorFondo:'#EEEDFE' },
  };
  if (r === 'direccion' || r === 'administracion' || r === 'administrador' || r === 'admin')
    return { principal: A.aria, agentes: [A.beep, A.axel, A.max, A.rex, A.bruno, A.nova, A.luna, A.vera] };
  if (r === 'supervisor')
    return { principal: A.max, agentes: [A.beep, A.rex, A.bruno] };
  if (r === 'tecnico')
    return { principal: A.max, agentes: [] };
  if (r === 'comercial')
    return { principal: A.axel, agentes: [A.beep, A.rex] };
  if (r === 'marketing')
    return { principal: A.luna, agentes: [A.beep] };
  if (r === 'almacen')
    return { principal: A.bruno, agentes: [A.beep, A.max] };
  return { principal: A.beep, agentes: [] };
}

// ============================================
// GEOMETRIA DEL BOTON FLOTANTE (se exporta a proposito)
// ============================================
// El boton es `position: fixed`, asi que flota SOBRE el contenido de cualquier pantalla. En movil
// se montaba encima de la esquina inferior derecha de las tarjetas y cortaba el monto (hallazgo de
// Don Porfirio, 2026-09-10). El arreglo NO es mover el boton: es que el contenido RESERVE el hueco
// que ocupa, para que ninguna fila quede atrapada debajo ni con la lista al final del scroll.
// Estas constantes son la UNICA fuente: si el boton cambia de tamano o de sitio, el hueco se ajusta
// solo (por eso se exportan en vez de repetir numeros en Dashboard.js).
export const FAB_ASISTENTE = {
  tamano: 56,          // ancho y alto del boton
  bottomMovil: 80,     // separacion al fondo de la ventana en movil (por encima de la barra inferior)
  bottomEscritorio: 24,
  separacion: 16,      // aire entre la ultima fila y el boton
  umbralMovil: 768,    // mismo breakpoint que usa `esMovil` aqui abajo y `esCelular` en Dashboard
};

// Hueco que el contenido debe dejar al final, medido desde el borde inferior de SU scrollport.
// `altoBarraInferior` = alto de la barra de navegacion inferior, que ya esta reservada aparte.
// En escritorio devuelve 0 a proposito: ahi el boton no atrapa contenido y Ricardo pidio
// explicitamente que el escritorio no cambie.
export function espacioReservadoAsistente(esCelular, altoBarraInferior = 0) {
  if (!esCelular) return 0;
  const topeDelBoton = FAB_ASISTENTE.bottomMovil + FAB_ASISTENTE.tamano; // borde superior del boton
  return Math.max(0, topeDelBoton - altoBarraInferior + FAB_ASISTENTE.separacion);
}

function AsistenteBeep({ onNavegar, usuario, rol, nombre, onAccion, oculto = false }) {
  const [abierto, setAbierto] = useState(false);
  const [modoLev, setModoLev] = useState(false);
  const [vistaTareas, setVistaTareas] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [nuevaTarea, setNuevaTarea] = useState('');
  const [grabandoTarea, setGrabandoTarea] = useState(false);
  const [tareas, setTareas] = useState(cargarTareas);
  const [mostrarLista, setMostrarLista] = useState(false);
  const [totalNoLeidos, setTotalNoLeidos] = useState(0);
  const [noLeidosPorAgente, setNoLeidosPorAgente] = useState({});
  const [chatAbierto, setChatAbierto] = useState(false);
  const [canalActivoId, setCanalActivoId] = useState(null);
  const [chatMinimizado, setChatMinimizado] = useState(false);
  const [esMovil, setEsMovil] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const panelRef = useRef(null);
  const botonRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setEsMovil(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const actualizar = () => {
      try {
        const noleidos = JSON.parse(localStorage.getItem('beep_canales_noleidos') || '{}');
        const total = Object.values(noleidos).reduce((s, v) => s + (v || 0), 0);
        setTotalNoLeidos(total);
        setNoLeidosPorAgente(noleidos);
      } catch {}
    };
    actualizar();
    window.addEventListener('beep:canales-noleidos', actualizar);
    window.addEventListener('storage', actualizar);
    return () => {
      window.removeEventListener('beep:canales-noleidos', actualizar);
      window.removeEventListener('storage', actualizar);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const agenteId = e.detail;
      setCanalActivoId(agenteId || 'beep');
      setChatAbierto(true);
    };
    window.addEventListener('beep:abrir-agente', handler);
    return () => window.removeEventListener('beep:abrir-agente', handler);
  }, []);

  useEffect(() => {
    const sync = () => setTareas(cargarTareas());
    window.addEventListener('beep:tareas-actualizadas', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('beep:tareas-actualizadas', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const bottomBoton = esMovil ? FAB_ASISTENTE.bottomMovil : FAB_ASISTENTE.bottomEscritorio;
  const rightBoton = 24;

  const pendientes = tareas.filter((t) => !t.hecha).length;

  const navegar = (ruta) => {
    setAbierto(false);
    setVistaTareas(false);
    if (onNavegar) onNavegar(ruta);
  };

  const abrirModoLevantamiento = () => {
    setAbierto(false);
    setVistaTareas(false);
    setModoLev(true);
  };

  const irNuevoRequerimiento = () => {
    setAbierto(false);
    setVistaTareas(false);
    if (onNavegar) onNavegar('requerimientos.nuevo');
  };

  const agregarTarea = () => {
    const texto = nuevaTarea.trim();
    if (!texto) return;
    const nueva = { id: Date.now(), texto, hecha: false, fecha: new Date().toISOString() };
    const actualizadas = [nueva, ...tareas];
    setTareas(actualizadas);
    guardarTareas(actualizadas);
    setNuevaTarea('');
  };

  const toggleTarea = (id) => {
    const actualizadas = tareas.map((t) =>
      t.id === id ? { ...t, hecha: !t.hecha } : t
    );
    setTareas(actualizadas);
    guardarTareas(actualizadas);
  };

  const eliminarTarea = (id) => {
    const actualizadas = tareas.filter((t) => t.id !== id);
    setTareas(actualizadas);
    guardarTareas(actualizadas);
  };

  const iniciarDictadoTarea = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Tu navegador no soporta reconocimiento de voz.');
      return;
    }
    if (grabandoTarea && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'es-MX';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const texto = e.results[0][0].transcript;
      setNuevaTarea((prev) => (prev ? prev + ' ' + texto : texto));
    };
    rec.onend = () => setGrabandoTarea(false);
    rec.onerror = () => setGrabandoTarea(false);
    recognitionRef.current = rec;
    setGrabandoTarea(true);
    rec.start();
  };

  const tareasOrdenadas = [
    ...tareas.filter((t) => !t.hecha),
    ...tareas.filter((t) => t.hecha),
  ];

  const onCrearDesdeIA = (lista) => {
    window.dispatchEvent(
      new CustomEvent('beep:crearDesdeIA', { detail: lista })
    );
    setModoLev(false);
    if (onNavegar) onNavegar('requerimientos.nuevo');
  };

  // MOVIL-GUARDAR (2026-09-12): con una ventana modal abierta el boton flotante se montaba encima
  // de sus controles (tapaba "+ Crear cliente nuevo" y el monto de las tarjetas). Mientras haya
  // modal, el asistente entero desaparece; al cerrarla vuelve solo. No se mueve de sitio ni cambia
  // su funcion: simplemente cede el paso. `oculto` lo decide el Dashboard con la senal que ya
  // existia para esconder la barra inferior (<html> con overflow:hidden).
  if (oculto) return null;

  return (
    <>
      {/* Overlay para cerrar lista */}
      {mostrarLista && (
        <div
          onClick={() => setMostrarLista(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 999, background: 'transparent',
          }}
        />
      )}

      {/* Panel flotante con lista de agentes */}
      {mostrarLista && (() => {
        const { principal, agentes: otrosAgentes } = getAgentesParaRol(rol);
        const abrirAgente = (agenteId) => {
          setMostrarLista(false);
          setCanalActivoId(agenteId);
          setChatAbierto(true);
        };
        const noLPrincipal = noLeidosPorAgente[principal.id] || 0;
        return (
        <div style={{
          position: 'fixed',
          bottom: (typeof bottomBoton === 'number' ? bottomBoton : 24) + 70,
          right: typeof rightBoton === 'number' ? rightBoton : 24,
          width: 250, padding: 12,
          background: '#fff', borderRadius: 16,
          border: '0.5px solid #eee',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          zIndex: 1001, fontFamily: "'Poppins', sans-serif",
          maxHeight: '70vh', overflowY: 'auto',
        }}>
          {totalNoLeidos > 0 && (
            <div style={{ fontSize: 11, color: '#EF4444', fontWeight: 700, marginBottom: 8, paddingLeft: 4 }}>
              🔴 {totalNoLeidos} mensaje{totalNoLeidos === 1 ? '' : 's'} nuevo{totalNoLeidos === 1 ? '' : 's'}
            </div>
          )}
          <div onClick={() => abrirAgente(principal.id)} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: 10, borderRadius: 12, cursor: 'pointer',
            background: principal.colorFondo, marginBottom: 8,
            border: `1px solid ${principal.color}33`,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: '#fff',
              border: `2px solid ${principal.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, flexShrink: 0,
            }}>{principal.emoji}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: principal.color }}>{principal.nombre}</div>
              <div style={{ fontSize: 11, color: principal.color, opacity: 0.7 }}>{principal.funcion}</div>
            </div>
            {noLPrincipal > 0 && (
              <span style={{ background:'#25D366', color:'#fff', borderRadius:999, minWidth:20, height:20, padding:'0 6px', fontSize:10, fontWeight:800, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{noLPrincipal}</span>
            )}
          </div>
          {otrosAgentes.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6, paddingLeft: 4 }}>Otros agentes</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {otrosAgentes.map(a => {
                  const noL = noLeidosPorAgente[a.id] || 0;
                  return (
                    <div key={a.id} onClick={() => abrirAgente(a.id)} style={{
                      display:'flex', alignItems:'center', gap:10, padding:'7px 6px', borderRadius:10,
                      cursor:'pointer', background: noL > 0 ? '#F0FFF4' : 'transparent',
                    }}
                      onMouseEnter={e => { if (!noL) e.currentTarget.style.background = '#f5f6f7'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = noL > 0 ? '#F0FFF4' : 'transparent'; }}
                    >
                      <div style={{ width:36, height:36, borderRadius:'50%', background:a.colorFondo, border:`1px solid ${a.color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{a.emoji}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, color:'#1a2a3a', fontWeight: noL > 0 ? 700 : 600 }}>{a.nombre}</div>
                        <div style={{ fontSize:10, color:'#999' }}>{a.funcion}</div>
                      </div>
                      {noL > 0 && (
                        <span style={{ background:'#25D366', color:'#fff', borderRadius:999, minWidth:20, height:20, padding:'0 6px', fontSize:10, fontWeight:800, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{noL}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        );
      })()}

      {/* Botón flotante */}
      <button
        ref={botonRef}
        onClick={() => {
          setMostrarLista(v => !v);
        }}
        aria-label="Mis agentes"
        style={{
          position: 'fixed',
          bottom: bottomBoton,
          right: rightBoton,
          width: FAB_ASISTENTE.tamano,
          height: FAB_ASISTENTE.tamano,
          borderRadius: '50%',
          background: '#43ed5b',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          boxShadow: totalNoLeidos > 0 ? '0 0 0 0 rgba(67,237,91,0.7)' : '0 4px 20px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          transition: 'transform 0.2s ease',
          transform: mostrarLista ? 'scale(0.92)' : 'scale(1)',
          padding: 0,
          animation: totalNoLeidos > 0 ? 'beepFabPulse 2s ease-in-out infinite' : 'none',
        }}
      >
        <style>{`@keyframes beepFabPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(67,237,91,0.5); } 50% { box-shadow: 0 0 0 10px rgba(67,237,91,0); } }`}</style>
        <img
          src={encodeURI('/icono beep blanco fondo trasparente.png')}
          style={{ width: '28px', height: '28px', objectFit: 'contain' }}
          alt="agente"
        />
        {totalNoLeidos > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -2, right: -2,
              minWidth: 20, height: 20, padding: '0 5px',
              borderRadius: 10,
              background: '#EF4444',
              color: '#fff',
              fontSize: 11, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #fff',
            }}
          >
            {totalNoLeidos}
          </span>
        )}
      </button>

      {abierto && (
        <AgenteBeep
          usuario={usuario}
          rol={rol}
          nombre={nombre}
          onNavegar={(ruta, tab) => {
            setAbierto(false);
            if (onNavegar) onNavegar(ruta, tab);
          }}
          onAccion={onAccion}
          onCerrar={() => setAbierto(false)}
          esMovil={esMovil}
        />
      )}

      {/* Panel antiguo (oculto) */}
      <div
        ref={panelRef}
        hidden
        style={{
          position: 'fixed',
          bottom: bottomBoton + 68,
          right: rightBoton,
          width: 300,
          maxWidth: 'calc(100vw - 32px)',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          padding: 12,
          zIndex: 1000,
          transformOrigin: 'bottom right',
          transform: abierto ? 'scale(1)' : 'scale(0.85)',
          opacity: abierto ? 1 : 0,
          pointerEvents: abierto ? 'auto' : 'none',
          transition: 'transform 0.22s ease, opacity 0.22s ease',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px 12px',
            borderBottom: `1px solid ${COLOR_PRINCIPAL}22`,
            marginBottom: 8,
            color: COLOR_OSCURO,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          <span>{vistaTareas ? 'Mis tareas' : 'Asistente Beep'}</span>
          {vistaTareas && (
            <button
              onClick={() => setVistaTareas(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: COLOR_PRINCIPAL,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              ← Volver
            </button>
          )}
        </div>

        {!vistaTareas && (
          <>
            <button
              onClick={abrirModoLevantamiento}
              style={opcionBtn}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${COLOR_PRINCIPAL}18`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
            >
              <span style={{ fontSize: 20 }}>🎙️</span>
              <span>Modo levantamiento</span>
            </button>

            <button
              onClick={irNuevoRequerimiento}
              style={opcionBtn}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${COLOR_PRINCIPAL}18`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
            >
              <span style={{ fontSize: 20 }}>➕</span>
              <span>Nuevo requerimiento</span>
            </button>

            {/* Buscador */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <span style={{ fontSize: 20, flexShrink: 0 }}>🔍</span>
              <input
                type="text"
                placeholder="Buscar..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            <button
              onClick={() => navegar('inicio.midia')}
              style={opcionBtn}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${COLOR_PRINCIPAL}18`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
            >
              <span style={{ fontSize: 20 }}>☀️</span>
              <span>Mi día</span>
            </button>

            <button
              onClick={() => setVistaTareas(true)}
              style={opcionBtn}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${COLOR_PRINCIPAL}18`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
            >
              <span style={{ fontSize: 20 }}>📝</span>
              <span style={{ flex: 1, textAlign: 'left' }}>Mis tareas</span>
              {pendientes > 0 && (
                <span
                  style={{
                    background: COLOR_ROJO,
                    color: '#fff',
                    borderRadius: 10,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {pendientes}
                </span>
              )}
            </button>
          </>
        )}

        {vistaTareas && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                display: 'flex',
                gap: 6,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <input
                type="text"
                placeholder="Nueva tarea rápida..."
                value={nuevaTarea}
                onChange={(e) => setNuevaTarea(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && agregarTarea()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: 'border-box',
                  border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <button
                onClick={iniciarDictadoTarea}
                title="Dictar tarea"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: 'none',
                  background: grabandoTarea ? COLOR_ROJO : COLOR_PRINCIPAL,
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                🎤
              </button>
              <button
                onClick={agregarTarea}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: 'none',
                  background: COLOR_OSCURO,
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                +
              </button>
            </div>

            <div
              style={{
                maxHeight: 280,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginTop: 4,
              }}
            >
              {tareasOrdenadas.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px 10px',
                    color: '#888',
                    fontSize: 13,
                  }}
                >
                  No tienes tareas pendientes 🎉
                </div>
              )}
              {tareasOrdenadas.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: t.hecha ? '#f6f7f8' : '#fff',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={t.hecha}
                    onChange={() => toggleTarea(t.id)}
                    style={{ cursor: 'pointer', accentColor: COLOR_VERDE }}
                  />
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: t.hecha ? '#999' : '#222',
                      textDecoration: t.hecha ? 'line-through' : 'none',
                      wordBreak: 'break-word',
                    }}
                  >
                    {t.texto}
                  </span>
                  <button
                    onClick={() => eliminarTarea(t.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#bbb',
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                    title="Eliminar"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {modoLev && (
        <ModoLevantamiento
          abierto={modoLev}
          onCerrar={() => setModoLev(false)}
          onCrear={onCrearDesdeIA}
          esMovil={esMovil}
        />
      )}

      {/* Burbuja minimizada */}
      {chatAbierto && chatMinimizado && (() => {
        const { principal, agentes: oa } = getAgentesParaRol(rol);
        const all = [principal, ...oa];
        const info = all.find(a => a.id === canalActivoId) || principal;
        return (
          <div onClick={() => setChatMinimizado(false)} style={{
            position: 'fixed', bottom: esMovil ? 80 : 80, right: 16,
            background: info.color || '#185FA5', color: 'white', borderRadius: 24,
            padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer', zIndex: 9998,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: 13, fontWeight: 500,
            fontFamily: PP,
          }}>
            <span style={{ fontSize: 16 }}>{info.emoji}</span>
            {info.nombre}
            <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 99, padding: '1px 8px', fontSize: 11 }}>minimizado</span>
          </div>
        );
      })()}

      {/* Overlay para desktop detrás del chat panel */}
      {chatAbierto && !chatMinimizado && !esMovil && (
        <div onClick={() => setChatMinimizado(true)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)', zIndex: 9997,
        }} />
      )}

      {/* Panel flotante del chat */}
      {chatAbierto && !chatMinimizado && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: esMovil ? '100%' : 380,
          background: 'white', zIndex: 9998,
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
          animation: 'beepSlideIn 0.3s ease',
        }}>
          <style>{`@keyframes beepSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
          <CanalAgentes
            nombre={nombre}
            rol={rol}
            canalInicialId={canalActivoId}
            onNavegar={onNavegar}
            onCerrar={() => { setChatAbierto(false); setChatMinimizado(false); }}
            onMinimizar={() => setChatMinimizado(true)}
            modoPanel={true}
          />
        </div>
      )}
    </>
  );
}

const opcionBtn = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  background: 'transparent',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontSize: 14,
  color: '#222',
  textAlign: 'left',
  transition: 'background 0.15s ease',
  boxSizing: 'border-box',
};

export default AsistenteBeep;
